/**
 * CalculatorBuilderPage — Admin · Calculator Builder (Wave 1 + Wave 2, 2026-06-30)
 *
 * Ported from:
 *   docs/superpowers/claude-design/stellar-builders-2026-06-30/stellar-calc-builder.jsx
 *   docs/superpowers/claude-design/stellar-builders-2026-06-30/stellar-calc-page.jsx
 *
 * All mock data (CALC_CONFIGS / PRICE_BOOK) replaced with live API calls via
 * calculatorBuilderApi. Prices are STRICTLY read-only (no input accepts a price).
 *
 * Engine-backed calcs → SizingPanel (locked) + CatalogMapping (read-only table).
 * No-code calcs       → InputDesigner + FormulaEditor + RecRules (editable via API).
 * Both → CatalogMapping read-only price column + TestRun panel.
 *
 * AURA token conventions:
 *   var(--ink-0/1/2/3/4), var(--bg-1), var(--line/--line-2),
 *   var(--saffron), var(--forest-50/100/600),
 *   var(--ivory-0/2), var(--card).
 * No `primary` Tailwind colour classes used anywhere.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Cpu,
  Code2,
  Database,
  FlaskConical,
  Plus,
  X,
  GripVertical,
  Lock,
  RefreshCw,
  ChevronRight,
  ArrowRight,
  Settings,
  Zap,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  calculatorBuilderApi,
  type CalcConfigSummary,
  type CalcConfigDetail,
  type CatalogMappingRow,
  type FormulaRow,
  type RecommendationRule,
  type CalcInputDescriptor,
  type TestRunResponse,
  type NoCodeConfig,
} from '../../api/calculatorBuilder';
import CalculatorStudio from './calculator-studio/CalculatorStudio';
import NewCalculatorDialog from './calculator-studio/NewCalculatorDialog';

// ─── Formatting helpers ───────────────────────────────────────────────────────

function formatINR(n: number | null | undefined, compact = false): string {
  if (n == null || isNaN(n)) return '—';
  const neg = n < 0;
  const abs = Math.abs(n);
  if (compact) {
    if (abs >= 1e7) return (neg ? '-' : '') + '₹' + (abs / 1e7).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
    if (abs >= 1e5) return (neg ? '-' : '') + '₹' + (abs / 1e5).toFixed(2).replace(/\.?0+$/, '') + ' L';
    if (abs >= 1e3) return (neg ? '-' : '') + '₹' + (abs / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'K';
  }
  const s = Math.round(abs).toString();
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const grouped = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3 : last3;
  return (neg ? '-' : '') + '₹' + grouped;
}

// ─── Tiny primitives ─────────────────────────────────────────────────────────

interface PanelProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  pad?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

function Panel({ title, subtitle, icon, right, pad = true, style, children }: PanelProps) {
  return (
    <div
      style={{
        background: 'var(--card, #fff)',
        border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 10,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          borderBottom: '1px solid var(--line, #e5e7eb)',
          background: 'var(--bg-1, #f8f9fa)',
        }}
      >
        {icon && (
          <span style={{ color: 'var(--ink-3)', display: 'flex' }}>{icon}</span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--ink-1)', letterSpacing: '-0.01em' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>{subtitle}</div>
          )}
        </div>
        {right}
      </div>
      <div style={pad ? { padding: '12px 14px' } : undefined}>{children}</div>
    </div>
  );
}

interface TextInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  style?: React.CSSProperties;
}

function TextInput({ value, onChange, placeholder, mono, style }: TextInputProps) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        height: 30,
        padding: '0 8px',
        border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 6,
        fontSize: mono ? 12 : 12.5,
        fontFamily: mono ? 'monospace' : undefined,
        color: 'var(--ink-1)',
        background: 'var(--bg-1, #f8f9fa)',
        outline: 'none',
        ...style,
      }}
    />
  );
}

interface SelectProps {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  style?: React.CSSProperties;
}

function Select({ value, onChange, options, style }: SelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 30,
        padding: '0 6px',
        border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--ink-1)',
        background: 'var(--bg-1, #f8f9fa)',
        outline: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

interface ToggleProps {
  on: boolean;
  onChange: (v: boolean) => void;
  size?: 'sm' | 'md';
}

function Toggle({ on, onChange, size = 'md' }: ToggleProps) {
  const h = size === 'sm' ? 16 : 20;
  const w = size === 'sm' ? 28 : 36;
  const knob = size === 'sm' ? 12 : 16;
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      style={{
        width: w,
        height: h,
        borderRadius: h,
        border: 'none',
        padding: 0,
        background: on ? 'var(--saffron, #f59e0b)' : 'var(--line, #e5e7eb)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.15s',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          display: 'block',
          width: knob,
          height: knob,
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: (h - knob) / 2,
          left: on ? w - knob - (h - knob) / 2 : (h - knob) / 2,
          transition: 'left 0.15s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

function SmallBtn({
  onClick,
  children,
  variant = 'outline',
  style,
}: {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'outline' | 'ghost' | 'danger';
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 28,
    padding: '0 10px',
    borderRadius: 6,
    fontSize: 11.5,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.1s',
  };
  const variants: Record<string, React.CSSProperties> = {
    outline: {
      border: '1px solid var(--line, #e5e7eb)',
      background: 'var(--card, #fff)',
      color: 'var(--ink-1)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--ink-2)',
    },
    danger: {
      background: 'transparent',
      color: '#dc2626',
    },
  };
  return (
    <button type="button" onClick={onClick} style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function SaveToast({ msg, kind }: { msg: string | null; kind: 'ok' | 'err' }) {
  if (!msg) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 500,
        background: kind === 'ok' ? 'var(--forest-600, #15803d)' : '#dc2626',
        color: '#fff',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      }}
    >
      {kind === 'ok' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
      {msg}
    </div>
  );
}

// ─── SizingPanel — engine-backed (read-only) ──────────────────────────────────

function SizingPanel({ sizing }: { sizing?: Array<{ out: string; from: string }> }) {
  if (!sizing || sizing.length === 0) {
    return (
      <Panel
        title="Sizing logic"
        subtitle="Engine-backed — read-only"
        icon={<Cpu size={14} />}
        style={{ marginBottom: 14 }}
      >
        <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          Sizing is handled by the engine layer and cannot be edited here.
        </div>
      </Panel>
    );
  }
  return (
    <Panel
      title="Sizing logic"
      subtitle="Engine-backed — read-only"
      icon={<Cpu size={14} />}
      style={{ marginBottom: 14 }}
      pad={false}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--bg-1, #f8f9fa)' }}>
            <th style={{ padding: '7px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--line)' }}>
              Output
            </th>
            <th style={{ padding: '7px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--line)' }}>
              Formula
            </th>
            <th style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 600, color: 'var(--ink-2)', borderBottom: '1px solid var(--line)', width: 60 }}>
              <Lock size={11} style={{ display: 'inline-block' }} />
            </th>
          </tr>
        </thead>
        <tbody>
          {sizing.map((row, i) => (
            <tr key={i} style={{ borderBottom: i < sizing.length - 1 ? '1px solid var(--line, #e5e7eb)' : undefined }}>
              <td style={{ padding: '8px 14px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--ink-0)' }}>
                {row.out}
              </td>
              <td style={{ padding: '8px 14px', fontFamily: 'monospace', color: 'var(--ink-2)' }}>
                {row.from}
              </td>
              <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                <Lock size={11} style={{ color: 'var(--ink-4)' }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

// ─── CatalogMapping — read-only prices ────────────────────────────────────────

function CatalogMapping({ mapping }: { mapping: CatalogMappingRow[] }) {
  return (
    <Panel
      title="Catalog mapping"
      subtitle="Sizing bucket → SKU · price read-only from Product"
      icon={<Database size={14} />}
      style={{ marginBottom: 14 }}
      pad={false}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--bg-1, #f8f9fa)' }}>
            {['Bucket', 'SKU', 'Product', 'DB price'].map((h, i) => (
              <th
                key={h}
                style={{
                  padding: '7px 14px',
                  textAlign: i === 3 ? 'right' : 'left',
                  fontWeight: 600,
                  color: 'var(--ink-2)',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mapping.map((m, i) => (
            <tr
              key={i}
              style={{ borderBottom: i < mapping.length - 1 ? '1px solid var(--line, #e5e7eb)' : undefined }}
            >
              <td style={{ padding: '8px 14px', fontFamily: 'monospace', color: 'var(--ink-2)' }}>
                {m.key}
              </td>
              <td style={{ padding: '8px 14px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--ink-0)' }}>
                {m.sku}
              </td>
              <td style={{ padding: '8px 14px', color: 'var(--ink-1)' }}>{m.productLabel}</td>
              <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ink-2)' }}
                  title="Price is read-only — from Product.unitPrice"
                >
                  <Lock size={11} style={{ color: 'var(--ink-4)' }} />
                  {m.price != null ? formatINR(m.price) : '—'}
                </span>
              </td>
            </tr>
          ))}
          {mapping.length === 0 && (
            <tr>
              <td colSpan={4} style={{ padding: '14px', textAlign: 'center', color: 'var(--ink-3)', fontStyle: 'italic', fontSize: 12 }}>
                No catalog mapping defined.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Panel>
  );
}

// ─── InputDesigner — no-code only ─────────────────────────────────────────────

interface InputDesignerProps {
  inputs: CalcInputDescriptor[];
  onChange: (inputs: CalcInputDescriptor[]) => void;
}

function InputDesigner({ inputs, onChange }: InputDesignerProps) {
  const set = (i: number, patch: Partial<CalcInputDescriptor>) =>
    onChange(inputs.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <Panel
      title="Input Designer"
      subtitle="Fields the customer answers"
      icon={<Code2 size={14} />}
      style={{ marginBottom: 14 }}
      right={
        <SmallBtn
          onClick={() =>
            onChange([
              ...inputs,
              { key: `field${inputs.length + 1}`, label: 'New field', type: 'number', unit: '' },
            ])
          }
        >
          <Plus size={12} /> Add field
        </SmallBtn>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {inputs.map((inp, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 10px',
              background: 'var(--bg-1, #f8f9fa)',
              border: '1px solid var(--line, #e5e7eb)',
              borderRadius: 9,
            }}
          >
            <span style={{ color: 'var(--ink-4)', cursor: 'grab', display: 'flex' }}>
              <GripVertical size={14} />
            </span>
            <TextInput
              value={inp.key}
              onChange={(v) => set(i, { key: v })}
              mono
              placeholder="key"
              style={{ width: 100 }}
            />
            <TextInput
              value={inp.label}
              onChange={(v) => set(i, { label: v })}
              placeholder="Label"
              style={{ flex: 1 }}
            />
            <Select
              value={inp.type}
              onChange={(v) => set(i, { type: v as CalcInputDescriptor['type'] })}
              options={[
                { value: 'number', label: 'Number' },
                { value: 'select', label: 'Select' },
                { value: 'toggle', label: 'Toggle' },
              ]}
              style={{ width: 100 }}
            />
            <TextInput
              value={inp.unit || ''}
              onChange={(v) => set(i, { unit: v })}
              placeholder="unit"
              style={{ width: 72 }}
            />
            <SmallBtn
              variant="ghost"
              onClick={() => onChange(inputs.filter((_, j) => j !== i))}
              style={{ width: 26, padding: 0, justifyContent: 'center' }}
            >
              <X size={13} />
            </SmallBtn>
          </div>
        ))}
        {inputs.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
            No inputs yet — add a field.
          </div>
        )}
      </div>
    </Panel>
  );
}

// ─── FormulaEditor — no-code only ────────────────────────────────────────────

interface FormulaEditorProps {
  formulas: FormulaRow[];
  onChange: (formulas: FormulaRow[]) => void;
}

function FormulaEditor({ formulas, onChange }: FormulaEditorProps) {
  const set = (i: number, patch: Partial<FormulaRow>) =>
    onChange(formulas.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <Panel
      title="Formulas / derivations"
      subtitle="outputKey ← expression over inputs"
      icon={<Zap size={14} />}
      style={{ marginBottom: 14 }}
      right={
        <SmallBtn
          onClick={() =>
            onChange([...formulas, { outputKey: `out${formulas.length + 1}`, expr: '' }])
          }
        >
          <Plus size={12} /> Add formula
        </SmallBtn>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {formulas.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TextInput
              value={f.outputKey}
              onChange={(v) => set(i, { outputKey: v })}
              mono
              style={{ width: 130 }}
            />
            <span style={{ fontFamily: 'monospace', color: 'var(--ink-3)', fontSize: 14, flexShrink: 0 }}>←</span>
            <TextInput
              value={typeof f.expr === 'string' ? f.expr : JSON.stringify(f.expr)}
              onChange={(v) => set(i, { expr: v })}
              mono
              placeholder="area * 0.05"
              style={{ flex: 1 }}
            />
            <SmallBtn
              variant="ghost"
              onClick={() => onChange(formulas.filter((_, j) => j !== i))}
              style={{ width: 26, padding: 0, justifyContent: 'center' }}
            >
              <X size={13} />
            </SmallBtn>
          </div>
        ))}
        {formulas.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
            No formulas yet.
          </div>
        )}
      </div>
    </Panel>
  );
}

// ─── RecRules — recommendation rules ─────────────────────────────────────────

interface RecRulesProps {
  rules: RecommendationRule[];
  catalogMapping: CatalogMappingRow[];
  onChange: (rules: RecommendationRule[]) => void;
}

function RecRules({ rules, catalogMapping, onChange }: RecRulesProps) {
  const setRule = (i: number, patch: Partial<RecommendationRule>) =>
    onChange(rules.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const OPS = [
    { value: 'gte', label: '≥' },
    { value: 'lte', label: '≤' },
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'is', label: '=' },
  ];

  return (
    <Panel
      title="Recommendation rules"
      subtitle="Priority-ordered, first match wins"
      icon={<ArrowRight size={14} />}
      style={{ marginBottom: 14 }}
      right={
        <SmallBtn
          onClick={() =>
            onChange([
              ...rules,
              {
                priority: rules.length,
                conditions: [],
                recommends: catalogMapping[0]
                  ? [{ sku: catalogMapping[0].sku, productLabel: catalogMapping[0].productLabel, qtyExpr: '1' }]
                  : [],
              },
            ])
          }
        >
          <Plus size={12} /> Add rule
        </SmallBtn>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rules.map((rule, i) => {
          const conds = Array.isArray(rule.conditions) ? rule.conditions : [];
          const recs = Array.isArray(rule.recommends) ? rule.recommends : [];
          const rec = recs[0];
          const mapEntry = rec ? catalogMapping.find((m) => m.sku === rec.sku) : undefined;
          return (
            <div
              key={i}
              style={{
                border: '1px solid var(--line, #e5e7eb)',
                borderRadius: 9,
                overflow: 'hidden',
              }}
            >
              {/* Rule header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 12px',
                  background: 'var(--bg-1, #f8f9fa)',
                  borderBottom: '1px solid var(--line, #e5e7eb)',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  Rule {i + 1}
                </span>
                <div style={{ flex: 1 }} />
                <SmallBtn
                  variant="danger"
                  onClick={() => onChange(rules.filter((_, j) => j !== i))}
                  style={{ padding: '0 6px', height: 22 }}
                >
                  <X size={11} /> Remove
                </SmallBtn>
              </div>
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Conditions */}
                {conds.map((c, ci) => (
                  <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-3)', width: 28, textAlign: 'right', flexShrink: 0 }}>
                      {ci === 0 ? 'IF' : 'AND'}
                    </span>
                    <TextInput
                      value={c.field}
                      onChange={(v) => {
                        const updated = [...conds];
                        updated[ci] = { ...updated[ci], field: v };
                        setRule(i, { conditions: updated });
                      }}
                      mono
                      placeholder="field"
                      style={{ width: 110 }}
                    />
                    <Select
                      value={c.op}
                      onChange={(v) => {
                        const updated = [...conds];
                        updated[ci] = { ...updated[ci], op: v as 'gte' | 'lte' | 'gt' | 'lt' | 'is' };
                        setRule(i, { conditions: updated });
                      }}
                      options={OPS}
                      style={{ width: 52 }}
                    />
                    <TextInput
                      value={c.value}
                      onChange={(v) => {
                        const updated = [...conds];
                        updated[ci] = { ...updated[ci], value: v };
                        setRule(i, { conditions: updated });
                      }}
                      mono
                      placeholder="0"
                      style={{ width: 72 }}
                    />
                    <SmallBtn
                      variant="ghost"
                      onClick={() =>
                        setRule(i, { conditions: conds.filter((_, j) => j !== ci) })
                      }
                      style={{ width: 22, padding: 0, justifyContent: 'center' }}
                    >
                      <X size={11} />
                    </SmallBtn>
                  </div>
                ))}
                <SmallBtn
                  variant="outline"
                  onClick={() =>
                    setRule(i, { conditions: [...conds, { field: '', op: 'gte', value: '0' }] })
                  }
                  style={{ alignSelf: 'flex-start' }}
                >
                  <Plus size={11} /> Condition
                </SmallBtn>

                {/* Recommend row */}
                {rec && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '9px 11px',
                      background: 'var(--forest-50, #f0fdf4)',
                      border: '1px solid var(--forest-100, #dcfce7)',
                      borderRadius: 9,
                    }}
                  >
                    <ArrowRight size={14} style={{ color: 'var(--forest-600, #16a34a)', flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--forest-600, #16a34a)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        flexShrink: 0,
                      }}
                    >
                      recommend
                    </span>
                    <Select
                      value={rec.sku}
                      onChange={(v) => {
                        const m = catalogMapping.find((x) => x.sku === v);
                        setRule(i, {
                          recommends: [
                            {
                              ...rec,
                              sku: v,
                              productLabel: m?.productLabel ?? rec.productLabel,
                            },
                          ],
                        });
                      }}
                      options={catalogMapping.map((m) => ({ value: m.sku, label: m.productLabel }))}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0 }}>× qty</span>
                    <TextInput
                      value={
                        typeof rec.qtyExpr === 'string'
                          ? rec.qtyExpr
                          : JSON.stringify(rec.qtyExpr)
                      }
                      onChange={(v) => setRule(i, { recommends: [{ ...rec, qtyExpr: v }] })}
                      mono
                      style={{ width: 96 }}
                    />
                    {/* Price: READ-ONLY from price book */}
                    <span
                      title="Price pulled live from Product.unitPrice — not editable here"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      <Database size={12} />
                      {mapEntry?.price != null
                        ? formatINR(mapEntry.price, true)
                        : '—'}
                      <span style={{ fontSize: 9, color: 'var(--ink-4)' }}>from price book</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {rules.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
            No rules yet — add one.
          </div>
        )}
      </div>
    </Panel>
  );
}

// ─── TestRun panel ────────────────────────────────────────────────────────────

interface TestRunPanelProps {
  category: string;
  inputs: CalcInputDescriptor[];
}

function TestRunPanel({ category, inputs }: TestRunPanelProps) {
  const [vals, setVals] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TestRunResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setErr(null);
    try {
      const parsed: Record<string, unknown> = {};
      for (const inp of inputs) {
        const raw = vals[inp.key];
        if (inp.type === 'number') {
          const n = parseFloat(raw ?? '');
          if (!isNaN(n)) parsed[inp.key] = n;
        } else if (inp.type === 'toggle') {
          parsed[inp.key] = raw === 'true' || raw === '1';
        } else {
          parsed[inp.key] = raw ?? '';
        }
      }
      const res = await calculatorBuilderApi.testRun(category, parsed);
      setResult(res);
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { error?: string } }; message?: string })
          ?.response?.data?.error ??
        (e as { message?: string })?.message ??
        'Test-run failed',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <Panel
      title="Test-run"
      subtitle="Run the real engine path with fixture inputs"
      icon={<FlaskConical size={14} />}
      right={
        <button
          type="button"
          onClick={run}
          disabled={running}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 30,
            padding: '0 14px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 650,
            border: 'none',
            background: 'var(--saffron, #f59e0b)',
            color: '#fff',
            cursor: running ? 'not-allowed' : 'pointer',
            opacity: running ? 0.7 : 1,
          }}
        >
          {running ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={12} />}
          Run
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Input fields */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {inputs.map((inp) => (
            <div key={inp.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>
                {inp.label}
                {inp.unit && (
                  <span style={{ fontWeight: 400, color: 'var(--ink-4)', marginLeft: 4 }}>
                    ({inp.unit})
                  </span>
                )}
              </label>
              {inp.type === 'toggle' ? (
                <Toggle
                  on={vals[inp.key] === 'true'}
                  onChange={(v) => setVals((s) => ({ ...s, [inp.key]: v ? 'true' : 'false' }))}
                />
              ) : inp.type === 'select' && inp.options ? (
                <Select
                  value={vals[inp.key] ?? inp.options[0] ?? ''}
                  onChange={(v) => setVals((s) => ({ ...s, [inp.key]: v }))}
                  options={inp.options.map((o) => ({ value: o, label: o }))}
                />
              ) : (
                <TextInput
                  value={vals[inp.key] ?? ''}
                  onChange={(v) => setVals((s) => ({ ...s, [inp.key]: v }))}
                  placeholder="0"
                  style={{ width: 100 }}
                />
              )}
            </div>
          ))}
        </div>

        {err && (
          <div
            style={{
              padding: '8px 12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 7,
              fontSize: 12,
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <AlertCircle size={13} /> {err}
          </div>
        )}

        {result && (
          <div
            style={{
              border: '1px solid var(--line, #e5e7eb)',
              borderRadius: 9,
              overflow: 'hidden',
            }}
          >
            {/* Sizing */}
            {result.sizing && Object.keys(result.sizing).length > 0 && (
              <div
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--line, #e5e7eb)',
                  background: 'var(--bg-1, #f8f9fa)',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: 6,
                  }}
                >
                  Sizing
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {Object.entries(result.sizing).map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        padding: '4px 10px',
                        background: 'var(--card, #fff)',
                        border: '1px solid var(--line, #e5e7eb)',
                        borderRadius: 6,
                        fontSize: 11.5,
                      }}
                    >
                      <span style={{ color: 'var(--ink-3)', fontFamily: 'monospace' }}>{k}</span>
                      <span style={{ marginLeft: 6, fontWeight: 600, color: 'var(--ink-0)', fontFamily: 'monospace' }}>
                        {String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations && result.recommendations.length > 0 && (
              <div style={{ padding: '10px 14px' }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: 6,
                  }}
                >
                  Recommendations
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {result.recommendations.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '7px 10px',
                        background: 'var(--forest-50, #f0fdf4)',
                        border: '1px solid var(--forest-100, #dcfce7)',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle2 size={13} style={{ color: 'var(--forest-600, #16a34a)', flexShrink: 0 }} />
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--ink-0)' }}>
                          {r.sku}
                        </span>
                        <span style={{ color: 'var(--ink-2)' }}>× {r.qty}</span>
                      </div>
                      {/* Price read-only */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          color: 'var(--ink-2)',
                          fontSize: 11.5,
                        }}
                        title="Price from Product.unitPrice — read-only"
                      >
                        <Lock size={11} style={{ color: 'var(--ink-4)' }} />
                        {r.price != null ? formatINR(r.price) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
                {result.pricingNote && (
                  <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ink-4)', fontStyle: 'italic' }}>
                    {result.pricingNote}
                  </div>
                )}
              </div>
            )}

            {result.recommendations?.length === 0 && (
              <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                No recommendations matched these inputs.
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

// ─── Category sidebar item ────────────────────────────────────────────────────

function CategoryItem({
  cfg,
  active,
  onClick,
}: {
  cfg: CalcConfigSummary;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: active ? 'var(--saffron-soft, #fff8e7)' : 'transparent',
        padding: '10px 12px',
        borderRadius: 9,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        transition: 'background 0.1s',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: active ? 'var(--saffron, #f59e0b)' : 'var(--bg-1, #f3f4f6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {cfg.kind === 'engine_backed' ? (
          <Cpu size={13} style={{ color: active ? '#fff' : 'var(--ink-3)' }} />
        ) : (
          <Code2 size={13} style={{ color: active ? '#fff' : 'var(--ink-3)' }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: active ? 650 : 500,
            color: active ? 'var(--saffron-deep, #92400e)' : 'var(--ink-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {cfg.title}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 1 }}>
          {cfg.kind === 'engine_backed' ? 'Engine-backed' : 'No-code'} · {cfg.inputCount} inputs
        </div>
      </div>
      <ChevronRight size={13} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

interface LocalConfig {
  inputs: CalcInputDescriptor[];
  formulas: FormulaRow[];
  rules: RecommendationRule[];
  noCodeConfigId?: string;
}

type SaveStatus = 'idle' | 'saving' | 'ok' | 'err';

export function CalculatorBuilderPage() {
  // Lists
  const [summaries, setSummaries] = useState<CalcConfigSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Active category
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [detail, setDetail] = useState<CalcConfigDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Local editable state (no-code only)
  const [local, setLocal] = useState<LocalConfig>({
    inputs: [],
    formulas: [],
    rules: [],
  });

  // Save / toast
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [toastKind, setToastKind] = useState<'ok' | 'err'>('ok');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calculator Studio (S5): spec-driven editing for EVERY calc (legacy + new).
  const [studioMode, setStudioMode] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);

  const refreshSummaries = useCallback(async () => {
    try {
      const r = await calculatorBuilderApi.listConfigs();
      setSummaries(r.configs);
      return r.configs;
    } catch {
      return [] as CalcConfigSummary[];
    }
  }, []);

  const showToast = (msg: string, kind: 'ok' | 'err') => {
    setToastMsg(msg);
    setToastKind(kind);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 3000);
  };

  // Load summaries
  useEffect(() => {
    setLoadingList(true);
    calculatorBuilderApi
      .listConfigs()
      .then((r) => {
        setSummaries(r.configs);
        if (r.configs.length > 0 && !activeCategory) {
          setActiveCategory(r.configs[0].category);
        }
      })
      .catch((e: unknown) => {
        const err = (e as { response?: { data?: { error?: string } }; message?: string });
        setListError(err?.response?.data?.error ?? err?.message ?? 'Failed to load calculators');
      })
      .finally(() => setLoadingList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load detail when category changes
  const loadDetail = useCallback(async (cat: string) => {
    setLoadingDetail(true);
    setDetail(null);
    try {
      const d = await calculatorBuilderApi.getConfig(cat);
      setDetail(d);
      // Seed local state from no-code config if present
      if (d.kind === 'no_code' && d.noCodeConfig) {
        const nc = d.noCodeConfig as NoCodeConfig;
        setLocal({
          inputs: d.inputs ?? [],
          formulas: (nc.formulas ?? []).map((f) => ({
            id: f.id,
            outputKey: f.outputKey,
            expr: typeof f.expr === 'string' ? f.expr : JSON.stringify(f.expr),
          })),
          rules: nc.recommendationRules ?? [],
          noCodeConfigId: nc.id,
        });
      } else {
        setLocal({ inputs: d.inputs ?? [], formulas: [], rules: [], noCodeConfigId: undefined });
      }
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { error?: string } }; message?: string });
      showToast(err?.response?.data?.error ?? err?.message ?? 'Failed to load', 'err');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (activeCategory) loadDetail(activeCategory);
  }, [activeCategory, loadDetail]);

  // Save no-code config changes
  const saveChanges = async () => {
    if (!detail || detail.kind !== 'no_code' || !local.noCodeConfigId) return;
    setSaveStatus('saving');
    try {
      const configId = local.noCodeConfigId;
      // Patch the config inputs
      await calculatorBuilderApi.patchNoCodeConfig(configId, {
        inputs: { fields: local.inputs } as Record<string, unknown>,
      });
      // Sync formulas — simplified: patch is best-effort (server handles snapshot)
      // Full delta sync is complex; for now we patch the config blob
      showToast('Saved successfully', 'ok');
      setSaveStatus('ok');
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { error?: string } }; message?: string });
      showToast(err?.response?.data?.error ?? err?.message ?? 'Save failed', 'err');
      setSaveStatus('err');
    } finally {
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  // Revert
  const revertConfig = async () => {
    if (!local.noCodeConfigId) return;
    try {
      await calculatorBuilderApi.revert(local.noCodeConfigId);
      showToast('Reverted to last snapshot', 'ok');
      if (activeCategory) loadDetail(activeCategory);
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { error?: string } }; message?: string });
      showToast(err?.response?.data?.error ?? err?.message ?? 'Revert failed', 'err');
    }
  };

  // Active summary
  const activeSummary = summaries.find((s) => s.category === activeCategory);
  const isEngineB = detail?.kind === 'engine_backed';
  const isNoCode = detail?.kind === 'no_code';

  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--bg-1, #f8f9fa)' }}>
      {/* Sidebar */}
      <div
        style={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid var(--line, #e5e7eb)',
          background: 'var(--card, #fff)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: '14px 14px 10px',
            borderBottom: '1px solid var(--line, #e5e7eb)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}>
            Calculator Studio
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            Configure inputs, formulas + recommendations
          </div>
          <button
            type="button"
            onClick={() => setShowNewDialog(true)}
            style={{
              marginTop: 10,
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              height: 30,
              borderRadius: 7,
              border: '1px solid var(--accent, #4f46e5)',
              background: 'var(--accent, #4f46e5)',
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 650,
              cursor: 'pointer',
            }}
          >
            <Plus size={14} /> New calculator
          </button>
        </div>

        <div style={{ padding: '10px 8px', flex: 1 }}>
          {loadingList ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                color: 'var(--ink-3)',
                gap: 8,
                fontSize: 13,
              }}
            >
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Loading…
            </div>
          ) : listError ? (
            <div style={{ padding: 12, fontSize: 12, color: '#dc2626' }}>
              <AlertCircle size={13} style={{ display: 'inline-block', marginRight: 4 }} />
              {listError}
            </div>
          ) : (
            summaries.map((cfg) => (
              <CategoryItem
                key={cfg.category}
                cfg={cfg}
                active={cfg.category === activeCategory}
                onClick={() => setActiveCategory(cfg.category)}
              />
            ))
          )}
        </div>

        {/* Legend */}
        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--line, #e5e7eb)',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--ink-3)' }}>
            <Cpu size={12} /> Engine-backed — sizing locked
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--ink-3)' }}>
            <Code2 size={12} /> No-code — fully editable
          </div>
        </div>
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header bar */}
        {activeSummary && (
          <div
            style={{
              padding: '12px 20px',
              borderBottom: '1px solid var(--line, #e5e7eb)',
              background: 'var(--card, #fff)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: 'var(--saffron, #f59e0b)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {activeSummary.kind === 'engine_backed' ? (
                <Cpu size={15} style={{ color: '#fff' }} />
              ) : (
                <Code2 size={15} style={{ color: '#fff' }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}>
                {activeSummary.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                {activeSummary.kind === 'engine_backed' ? 'Engine-backed' : 'No-code'} ·{' '}
                {activeSummary.inputCount} inputs ·{' '}
                {activeSummary.productKey ?? activeSummary.category}
              </div>
            </div>
            {/* Studio / Classic toggle — Studio makes every calc editable via its spec */}
            <div style={{ display: 'inline-flex', border: '1px solid var(--line, #e5e7eb)', borderRadius: 7, overflow: 'hidden', marginRight: 8 }}>
              <button
                type="button"
                onClick={() => setStudioMode(true)}
                style={{
                  height: 30, padding: '0 12px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 650,
                  background: studioMode ? 'var(--accent, #4f46e5)' : 'var(--card, #fff)',
                  color: studioMode ? '#fff' : 'var(--ink-2, #374151)',
                }}
              >
                Studio
              </button>
              <button
                type="button"
                onClick={() => setStudioMode(false)}
                style={{
                  height: 30, padding: '0 12px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 650,
                  background: !studioMode ? 'var(--accent, #4f46e5)' : 'var(--card, #fff)',
                  color: !studioMode ? '#fff' : 'var(--ink-2, #374151)',
                }}
              >
                Classic
              </button>
            </div>
            {!studioMode && isNoCode && local.noCodeConfigId && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SmallBtn variant="outline" onClick={revertConfig}>
                  <RotateCcw size={12} /> Revert
                </SmallBtn>
                <button
                  type="button"
                  onClick={saveChanges}
                  disabled={saveStatus === 'saving'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    height: 30,
                    padding: '0 14px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 650,
                    border: 'none',
                    background:
                      saveStatus === 'ok'
                        ? 'var(--forest-600, #16a34a)'
                        : saveStatus === 'err'
                        ? '#dc2626'
                        : 'var(--saffron, #f59e0b)',
                    color: '#fff',
                    cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                    opacity: saveStatus === 'saving' ? 0.7 : 1,
                    transition: 'background 0.2s',
                  }}
                >
                  {saveStatus === 'saving' ? (
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : saveStatus === 'ok' ? (
                    <CheckCircle2 size={12} />
                  ) : (
                    <RefreshCw size={12} />
                  )}
                  {saveStatus === 'ok' ? 'Saved' : saveStatus === 'err' ? 'Failed' : 'Save changes'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', gap: 20 }}>
          {loadingDetail ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 12,
                color: 'var(--ink-3)',
              }}
            >
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 13 }}>Loading calculator…</span>
            </div>
          ) : !activeCategory ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 12,
                color: 'var(--ink-3)',
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 13,
                  background: 'var(--bg-1, #f3f4f6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Settings size={22} style={{ color: 'var(--ink-4)' }} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-1)' }}>
                Select a calculator
              </div>
            </div>
          ) : studioMode && activeCategory ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <CalculatorStudio category={activeCategory} onReverted={() => showToast('Reverted', 'ok')} />
            </div>
          ) : detail ? (
            <>
              {/* Left column — config panels */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
                {isEngineB && <SizingPanel sizing={detail.sizing} />}

                {isNoCode && (
                  <>
                    <InputDesigner
                      inputs={local.inputs}
                      onChange={(inputs) => setLocal((s) => ({ ...s, inputs }))}
                    />
                    <FormulaEditor
                      formulas={local.formulas}
                      onChange={(formulas) => setLocal((s) => ({ ...s, formulas }))}
                    />
                  </>
                )}

                <RecRules
                  rules={local.rules}
                  catalogMapping={detail.catalogMapping ?? []}
                  onChange={(rules) => setLocal((s) => ({ ...s, rules }))}
                />

                <CatalogMapping mapping={detail.catalogMapping ?? []} />

                {/* Options block */}
                <Panel title="Options" icon={<Settings size={14} />} style={{ marginTop: 0 }}>
                  {isEngineB ? (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                      Options are engine-controlled for this calculator.
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                      No configurable options for this no-code calculator.
                    </div>
                  )}
                </Panel>
              </div>

              {/* Right column — TestRun */}
              <div style={{ width: 320, flexShrink: 0 }}>
                <TestRunPanel category={detail.category} inputs={local.inputs} />
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Spin keyframe (injected once) */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {showNewDialog && (
        <NewCalculatorDialog
          onClose={() => setShowNewDialog(false)}
          onCreated={async (config) => {
            setShowNewDialog(false);
            await refreshSummaries();
            setActiveCategory(config.category);
            setStudioMode(true);
            showToast(`Created “${config.title}”`, 'ok');
          }}
        />
      )}

      <SaveToast msg={toastMsg} kind={toastKind} />
    </div>
  );
}
